import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    InMemoryCache,
    ServerError,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import * as Sentry from "@sentry/react";
import { Observable } from "apollo-link";
import { onError } from "apollo-link-error";
import { SentryLink } from "apollo-link-sentry";
import ApolloLinkTimeout from "apollo-link-timeout";
import { GRAPHQL_MUTATION_REFRESH_AUTH_TOKEN } from "app/queries";
import {
    selectAuthToken,
    selectLoginAs,
    selectRefreshToken,
    updateAuthToken,
} from "features/auth/auth";
import { OperationDefinitionNode } from "graphql";
import _ from "lodash";
import { Store } from "redux";
import { analyticsTrack } from "./analytics/track";
import { isProduction, isRunningCypress } from "./config";
import { generateId } from "./generate-id";

const promiseToObservable = (promise: Promise<unknown>) => {
    return new Observable((subscriber) => {
        promise.then(
            (value) => {
                if (subscriber.closed) {
                    return;
                }
                subscriber.next(value);
                subscriber.complete();
            },
            (err) => {
                subscriber.error(err);
            },
        );
    });
};

const apolloServerUri = process.env.REACT_APP_APOLLO_SERVER_URL;

const httpLink = createHttpLink({
    uri: apolloServerUri,
});

const timeoutLink = new ApolloLinkTimeout(15000); // 15 second timeout

const errorLink = (store: Store, client: ApolloClient<unknown>): any =>
    onError(({ forward, networkError, graphQLErrors, operation, response }) => {
        const error = _.first(response?.errors)?.message;
        const refreshToken = selectRefreshToken(store.getState());

        if (error === "Token expired" && refreshToken) {
            const refreshPromise = client.mutate({
                mutation: GRAPHQL_MUTATION_REFRESH_AUTH_TOKEN,
                variables: {
                    refreshToken,
                },
            });

            return promiseToObservable(refreshPromise).flatMap((result: any) => {
                const { authToken } = result.data.refreshAuthToken;

                // update store and request headers
                store.dispatch(updateAuthToken({ authToken: authToken }));
                operation.setContext(({ headers = {} }) => ({
                    headers: {
                        ...headers,
                        authorization: `Bearer ${authToken}`,
                    },
                }));

                // retry request
                return forward(operation);
            });
        } else {
            const { startTime, requestProperties } = operation.getContext();
            const latency = Date.now() - startTime;
            const { statusCode, message, result } = (networkError as ServerError) || {};
            const errors = result?.errors;
            const { variables } = operation;

            // This may not be reliable (eg: connected to LAN but not internet)
            const isOffline = !window.navigator.onLine;

            const userError = graphQLErrors?.every((x) => x.extensions?.code === "UNAUTHENTICATED");

            if (!userError && !isOffline) {
                // Do not report authentication errors to Sentry
                Sentry.captureException(networkError || _.first(graphQLErrors), {
                    contexts: {
                        request: {
                            ...requestProperties,
                            variables,
                            statusCode,
                            message,
                            errors,
                            latency,
                            graphQLErrors,
                        },
                    },
                });
            }

            analyticsTrack(`request.error`, {
                ...requestProperties,
                statusCode,
                message,
                errors,
                latency,
                userError,
                isOffline,
            });
        }
    });

const authLink = (store: Store, client: ApolloClient<unknown>) =>
    new ApolloLink((operation, forward) => {
        const token = selectAuthToken(store.getState());
        const loginAsParameter = selectLoginAs(store.getState());

        // add the recent-activity custom header to the headers
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                ...(loginAsParameter ? { "x-login-as": loginAsParameter } : {}),
                authorization: token ? `Bearer ${token}` : "",
            },
        }));

        // respond to auth token expiration
        return forward(operation);
    });

const loggingLink = new ApolloLink((operation, forward) => {
    const requestId = generateId();
    const operationName = operation.operationName || "unknown";
    let innerOp = operation.query.definitions.find((x) => x.kind === "OperationDefinition") as
        | OperationDefinitionNode
        | undefined;
    let operationType = "";

    if (innerOp) {
        operationType = innerOp.operation;
    }

    const requestProperties = {
        name: operationName,
        type: operationType,
        requestId,
    };

    analyticsTrack("request.sent", requestProperties);

    const startTime = Date.now();

    operation.setContext(({ headers = {} }) => ({
        startTime,
        requestProperties,
        headers: {
            ...headers,
            "x-request-id": requestId,
        },
    }));

    return forward(operation).map((result) => {
        const latency = Date.now() - startTime;

        if (result.data) {
            analyticsTrack("request.success", {
                ...requestProperties,
                latency,
            });

            if (operationName === "unknown") {
                // If the graphQL operation is not named, log keys in the data to help us identify the query and add a name to it
                analyticsTrack("request.debugName", {
                    ...requestProperties,
                    keys: Object.keys(result.data),
                });

                if (!isProduction()) {
                    // eslint-disable-next-line no-console
                    console.log(
                        "Unnamed GraphQL operation should be given a name, result of operation:",
                        result.data,
                    );
                }
            }
        }

        if (result.errors) {
            analyticsTrack("request.successWithErrors", {
                ...requestProperties,
                errors: result.errors,
            });
        }

        return result;
    });
});

const MAX_RETRIES = 4;
const ERRORS_TO_RETRY = [
    "The network connection was lost.",
    "Timeout exceeded",
    "NetworkError when attempting to fetch resource.",
    "Failed to fetch",
    "The Internet connection appears to be offline.",
];
const retryLink = new RetryLink({
    attempts: (count, operation, error) => {
        if (count >= MAX_RETRIES) {
            return false;
        }
        return ERRORS_TO_RETRY.includes(error.message);
    },
});

export class AuthApolloClient<T> extends ApolloClient<T> {
    initialize(store: Store) {
        this.setLink(
            errorLink(store, this)
                .concat(authLink(store, this))
                .concat(new SentryLink())
                .concat(timeoutLink)
                .concat(loggingLink)
                .concat(retryLink)
                .concat(httpLink),
        );
    }
}

export const apolloClient = new AuthApolloClient({
    link: timeoutLink.concat(httpLink),
    cache: new InMemoryCache({
        addTypename: false,
        typePolicies: {},
    }),
});

// Expose apollo client to cypress for integration testing of graphql queries
if (isRunningCypress()) {
    (window as any).apolloClient = apolloClient;
}
